import { useState, useEffect, useRef } from "react";
import { MainLayout } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Brain,
  Send,
  Loader2,
  BarChart3,
  Users,
  BookOpen,
  TrendingUp,
  Sparkles,
  MessageSquare,
  History,
  RefreshCw,
  Zap,
  Target,
  Lightbulb,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = window.location.origin;

export default function MammouthAIPage() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [statsContext, setStatsContext] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [conversation, setConversation] = useState([]);
  const chatEndRef = useRef(null);

  useEffect(() => {
    loadStatsContext();
    loadHistory();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation]);

  const loadStatsContext = async () => {
    setLoadingStats(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/admin/mammouth/stats-context`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStatsContext(data);
      }
    } catch (error) {
      console.error("Errore caricamento stats:", error);
    } finally {
      setLoadingStats(false);
    }
  };

  const loadHistory = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/admin/mammouth/history?limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.queries || []);
      }
    } catch (error) {
      console.error("Errore caricamento history:", error);
    }
  };

  const analyzeData = async (customQuery = null) => {
    const queryText = customQuery || query;
    if (!queryText.trim()) {
      toast.error("Inserisci una domanda o query");
      return;
    }

    setLoading(true);
    setResponse(null);

    // Aggiungi alla conversazione
    const newUserMessage = { role: "user", content: queryText };
    setConversation(prev => [...prev, newUserMessage]);

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/admin/mammouth/analyze`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: queryText,
          context: statsContext?.context_text || "",
          model: "gpt-4o",
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setResponse(data.response);
        setConversation(prev => [...prev, { role: "assistant", content: data.response }]);
        setQuery("");
        loadHistory();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Errore nell'analisi");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  const quickQueries = [
    {
      label: "📊 Analisi Completa",
      query: "Analizza tutti i dati della piattaforma. Dammi un report completo con trend, punti di forza, aree di miglioramento e 5 azioni concrete da implementare.",
      icon: BarChart3,
    },
    {
      label: "👥 Analisi Utenti",
      query: "Analizza la distribuzione degli utenti per ruolo. Come posso aumentare le conversioni da potenziale cliente a cliente?",
      icon: Users,
    },
    {
      label: "📈 Trend Iscrizioni",
      query: "Analizza il trend delle iscrizioni. Quali corsi hanno più successo? Come posso migliorare il tasso di iscrizione?",
      icon: TrendingUp,
    },
    {
      label: "💡 Consigli Marketing",
      query: "Basandoti sui dati, dammi 5 strategie di marketing concrete per aumentare iscrizioni e engagement sulla piattaforma.",
      icon: Lightbulb,
    },
    {
      label: "🎯 KPI Chiave",
      query: "Quali sono i KPI più importanti da monitorare per ODR Academy? Crea un dashboard ideale con le metriche essenziali.",
      icon: Target,
    },
    {
      label: "🌿 Analisi Benessere",
      query: "Analizza la sezione Benessere: articoli, newsletter, potenziali clienti. Come posso migliorare l'engagement?",
      icon: Sparkles,
    },
  ];

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
              <Brain className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
                🦣 Mammouth AI
              </h1>
              <p className="text-muted-foreground">
                Analisi Dati Intelligente • Powered by GPT-4o
              </p>
            </div>
          </div>
          <Badge variant="secondary" className="bg-green-100 text-green-700">
            ✓ 100% Gratuito con Emergent LLM Key
          </Badge>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Colonna Sinistra - Stats Live */}
          <div className="lg:col-span-1 space-y-6">
            {/* Stats Card */}
            <Card className="border-2 border-violet-100">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <BarChart3 className="w-5 h-5 text-violet-600" />
                  Dati Live Piattaforma
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={loadStatsContext}
                    disabled={loadingStats}
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingStats ? "animate-spin" : ""}`} />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingStats ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
                  </div>
                ) : statsContext?.stats ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-blue-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-blue-600">
                          {statsContext.stats.corsi_totali}
                        </p>
                        <p className="text-xs text-blue-600/70">Corsi</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-green-600">
                          {statsContext.stats.iscrizioni_totali}
                        </p>
                        <p className="text-xs text-green-600/70">Iscrizioni</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-purple-600">
                          {statsContext.stats.utenti_totali}
                        </p>
                        <p className="text-xs text-purple-600/70">Utenti</p>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-amber-600">
                          {statsContext.stats.articoli_pubblicati}
                        </p>
                        <p className="text-xs text-amber-600/70">Articoli</p>
                      </div>
                    </div>
                    
                    <div className="pt-3 border-t">
                      <p className="text-xs text-muted-foreground mb-2">Distribuzione Utenti</p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span>👨‍💼 Clienti/Pro</span>
                          <span className="font-medium">{statsContext.stats.utenti_clienti}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>🌿 Potenziali</span>
                          <span className="font-medium">{statsContext.stats.potenziali_clienti}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>👨‍🏫 Docenti</span>
                          <span className="font-medium">{statsContext.stats.utenti_docenti}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>📰 Newsletter</span>
                          <span className="font-medium">{statsContext.stats.iscritti_newsletter}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">
                    Errore caricamento dati
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Quick Queries */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Zap className="w-5 h-5 text-amber-500" />
                  Query Rapide
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {quickQueries.map((q, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    className="w-full justify-start text-left h-auto py-2 px-3"
                    onClick={() => analyzeData(q.query)}
                    disabled={loading}
                  >
                    <q.icon className="w-4 h-4 mr-2 shrink-0" />
                    <span className="text-sm">{q.label}</span>
                  </Button>
                ))}
              </CardContent>
            </Card>

            {/* History */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setShowHistory(!showHistory)}
                >
                  <div className="flex items-center gap-2 text-lg">
                    <History className="w-5 h-5 text-slate-500" />
                    Storico Query
                  </div>
                  {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </CardTitle>
              </CardHeader>
              {showHistory && (
                <CardContent className="space-y-2 max-h-60 overflow-y-auto">
                  {history.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Nessuna query precedente
                    </p>
                  ) : (
                    history.map((h, idx) => (
                      <div
                        key={idx}
                        className="p-2 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => setQuery(h.query)}
                      >
                        <p className="text-sm line-clamp-2">{h.query}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(h.created_at).toLocaleString("it-IT")}
                        </p>
                      </div>
                    ))
                  )}
                </CardContent>
              )}
            </Card>
          </div>

          {/* Colonna Destra - Chat */}
          <div className="lg:col-span-2">
            <Card className="h-[calc(100vh-200px)] flex flex-col">
              <CardHeader className="pb-3 border-b">
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-violet-600" />
                  Chat con Mammouth AI
                </CardTitle>
              </CardHeader>
              
              {/* Messages Area */}
              <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                {conversation.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center p-8">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center mb-4">
                      <Brain className="w-10 h-10 text-violet-600" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">Ciao! Sono Mammouth AI 🦣</h3>
                    <p className="text-muted-foreground max-w-md">
                      Posso analizzare i dati della tua piattaforma ODR Academy e darti insights strategici.
                      Prova una delle query rapide o scrivi la tua domanda!
                    </p>
                  </div>
                ) : (
                  conversation.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                          msg.role === "user"
                            ? "bg-violet-600 text-white rounded-br-md"
                            : "bg-slate-100 text-slate-800 rounded-bl-md"
                        }`}
                      >
                        {msg.role === "assistant" && (
                          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-200">
                            <Brain className="w-4 h-4 text-violet-600" />
                            <span className="text-xs font-medium text-violet-600">Mammouth AI</span>
                          </div>
                        )}
                        <div className="whitespace-pre-wrap text-sm leading-relaxed">
                          {msg.content}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-violet-600" />
                        <span className="text-sm text-muted-foreground">Mammouth sta analizzando...</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </CardContent>

              {/* Input Area */}
              <div className="p-4 border-t bg-slate-50">
                <div className="flex gap-3">
                  <Textarea
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Scrivi la tua domanda... (es: Analizza il trend delle iscrizioni)"
                    className="resize-none min-h-[60px]"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        analyzeData();
                      }
                    }}
                  />
                  <Button
                    onClick={() => analyzeData()}
                    disabled={loading || !query.trim()}
                    className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 px-6"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Premi Invio per inviare • I dati vengono analizzati in tempo reale
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
